import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './Segmented.module.css';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: string;
  disabled?: boolean;
  title?: string;
  /**
   * A plan lock: greyed with a padlock but still clickable, the click going to `onLocked` (the
   * upgrade prompt) instead of `onChange`. Combine with `disabled` only for a lock nothing explains.
   */
  locked?: boolean;
  /** A small count in the option's top-right corner; nothing when absent or zero. */
  badge?: number;
  /** Colour the option takes when selected, where the choice carries a sign: `pos` for long,
   *  `neg` for short — the same tones `DirectionPill` uses, so the builder's selector reads like
   *  the saved strategy's label. Brand (the default) for choices with no sign. */
  tone?: 'pos' | 'neg';
}

export interface SegmentedProps<T extends string> {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** A click on a `locked` option. */
  onLocked?: (value: T) => void;
  /** `boxed`: a sunken track (view toggles). `chips`: free-standing chips (filters, tabs, metrics). */
  variant?: 'boxed' | 'chips';
  size?: 'sm' | 'md';
  mono?: boolean;
  ariaLabel: string;
  className?: string;
}

/** One of N choices. Used for view toggles, metric switches, filter chips and tabs alike. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  onLocked,
  variant = 'boxed',
  size = 'sm',
  mono,
  ariaLabel,
  className,
}: SegmentedProps<T>) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cx(s.group, s[variant], s[size], className)}>
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={(o.locked && !selected) || undefined}
            disabled={o.disabled}
            title={o.title}
            className={cx(s.option, selected && s.selected, selected && o.tone && s[`selected_${o.tone}`], mono && s.mono, o.locked && !selected && s.locked)}
            onClick={() => (o.locked && !selected ? onLocked?.(o.value) : onChange(o.value))}
          >
            {o.icon && <Icon name={o.icon} size={size === 'md' ? 18 : 16} />}
            {o.label}
            {o.locked && <Icon name="lock" size={13} />}
            {o.badge ? <span className={s.badge} aria-label={`${o.badge} here`}>{o.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
